// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import {
  Modal,
  Header,
  Segment,
  Grid,
  Label,
  Button,
  Icon,
  Message
} from 'semantic-ui-react';
import { AuditEvent, AuditSeverity } from '../types/audit';
import { formatDateTime } from '@loa/theme';

interface AuditEventDetailProps {
  event: AuditEvent;
  open: boolean;
  onClose: () => void;
}

function AuditEventDetail({ event, open, onClose }: Readonly<AuditEventDetailProps>) {
  const getSeverityColor = (severity: string): 'green' | 'yellow' | 'orange' | 'red' | 'grey' => {
    switch (severity) {
      case AuditSeverity.INFO:
        return 'green';
      case AuditSeverity.WARN:
        return 'yellow';
      case AuditSeverity.ERROR:
        return 'orange';
      case AuditSeverity.CRITICAL:
        return 'red';
      default:
        return 'grey';
    }
  };

  const formatJSON = (data?: Record<string, unknown>): string => {
    if (!data) return 'N/A';
    return JSON.stringify(data, null, 2);
  };

  return (
    <Modal open={open} onClose={onClose} size='large'>
      <Modal.Header>
        <Icon name='file alternate' />
        Audit Event Details
      </Modal.Header>
      <Modal.Content scrolling>
        {/* Basic Info */}
        <Segment>
          <Header as='h3'>
            <Icon name='info circle' />
            Event Information
          </Header>
          <Grid columns={2} divided>
            <Grid.Row>
              <Grid.Column>
                <strong>Event ID:</strong>
                <div style={{ marginTop: '5px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                  {event.eventId}
                </div>
              </Grid.Column>
              <Grid.Column>
                <strong>Timestamp:</strong>
                <div style={{ marginTop: '5px' }}>{formatDateTime(event.timestamp)}</div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <strong>Category:</strong>
                <div style={{ marginTop: '5px' }}>
                  <Label color='blue'>{event.category}</Label>
                </div>
              </Grid.Column>
              <Grid.Column>
                <strong>Action:</strong>
                <div style={{ marginTop: '5px' }}>
                  <Label>{event.action}</Label>
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <strong>Severity:</strong>
                <div style={{ marginTop: '5px' }}>
                  <Label color={getSeverityColor(event.severity)}>{event.severity}</Label>
                </div>
              </Grid.Column>
              <Grid.Column>
                <strong>Description:</strong>
                <div style={{ marginTop: '5px' }}>{event.description || 'N/A'}</div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {/* Actor Info */}
        <Segment>
          <Header as='h3'>
            <Icon name='user' />
            Actor (Who)
          </Header>
          <Grid columns={2} divided>
            <Grid.Row>
              <Grid.Column>
                <strong>User ID:</strong>
                <div style={{ marginTop: '5px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                  {event.actor.userId}
                </div>
              </Grid.Column>
              <Grid.Column>
                <strong>Username:</strong>
                <div style={{ marginTop: '5px' }}>{event.actor.username}</div>
              </Grid.Column>
            </Grid.Row>
            {event.actor.email && (
              <Grid.Row>
                <Grid.Column>
                  <strong>Email:</strong>
                  <div style={{ marginTop: '5px' }}>
                    <Icon name='mail' />
                    {event.actor.email}
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <strong>Role:</strong>
                  <div style={{ marginTop: '5px' }}>
                    <Label>{event.actor.role || 'N/A'}</Label>
                  </div>
                </Grid.Column>
              </Grid.Row>
            )}
            {event.actor.ipAddress && (
              <Grid.Row>
                <Grid.Column>
                  <strong>IP Address:</strong>
                  <div style={{ marginTop: '5px', fontFamily: 'monospace' }}>
                    {event.actor.ipAddress}
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <strong>User Agent:</strong>
                  <div
                    style={{
                      marginTop: '5px',
                      fontSize: '0.85em',
                      color: '#666',
                      wordBreak: 'break-word'
                    }}
                  >
                    {event.actor.userAgent || 'N/A'}
                  </div>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* Tenant Info */}
        <Segment>
          <Header as='h3'>
            <Icon name='building' />
            Tenant (Where)
          </Header>
          <Grid columns={2} divided>
            <Grid.Row>
              <Grid.Column>
                <strong>Brokerage ID:</strong>
                <div style={{ marginTop: '5px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                  {event.tenant.brokerageId}
                </div>
              </Grid.Column>
              <Grid.Column>
                <strong>Subdomain:</strong>
                <div style={{ marginTop: '5px' }}>{event.tenant.subdomain}</div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {/* Resource Info (only shown if resource exists) */}
        {event.resource && (
          <Segment>
            <Header as='h3'>
              <Icon name='folder' />
              Resource (What)
            </Header>
            <Grid columns={2} divided>
              <Grid.Row>
                <Grid.Column>
                  <strong>Resource Type:</strong>
                  <div style={{ marginTop: '5px' }}>
                    <Label color='blue'>{event.resource.type}</Label>
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <strong>Resource ID:</strong>
                  <div style={{ marginTop: '5px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                    {event.resource.id}
                  </div>
                </Grid.Column>
              </Grid.Row>
              {event.resource.name && (
                <Grid.Row>
                  <Grid.Column>
                    <strong>Resource Name:</strong>
                    <div style={{ marginTop: '5px' }}>{event.resource.name}</div>
                  </Grid.Column>
                  <Grid.Column />
                </Grid.Row>
              )}
            </Grid>
          </Segment>
        )}

        {/* Changes (if present) */}
        {event.changes && Object.keys(event.changes).length > 0 && (
          <Segment>
            <Header as='h3'>
              <Icon name='exchange' />
              Changes
            </Header>
            <pre
              style={{
                backgroundColor: '#f5f5f5',
                padding: '15px',
                borderRadius: '4px',
                overflow: 'auto',
                maxHeight: '300px',
                fontSize: '0.9em'
              }}
            >
              {formatJSON(event.changes)}
            </pre>
          </Segment>
        )}

        {/* Metadata */}
        <Segment>
          <Header as='h3'>
            <Icon name='cog' />
            Metadata
          </Header>
          <Grid columns={2} divided>
            {event.metadata.correlationId && (
              <Grid.Row>
                <Grid.Column>
                  <strong>Correlation ID:</strong>
                  <div style={{ marginTop: '5px', fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {event.metadata.correlationId}
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <strong>Session ID:</strong>
                  <div style={{ marginTop: '5px', fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {event.metadata.sessionId || 'N/A'}
                  </div>
                </Grid.Column>
              </Grid.Row>
            )}
            {event.metadata.transactionId && (
              <Grid.Row>
                <Grid.Column>
                  <strong>Transaction ID:</strong>
                  <div style={{ marginTop: '5px', fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {event.metadata.transactionId}
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <strong>Processed At:</strong>
                  <div style={{ marginTop: '5px' }}>
                    {formatDateTime(event.metadata.processedAt)}
                  </div>
                </Grid.Column>
              </Grid.Row>
            )}
            {event.metadata.signature && (
              <Grid.Row>
                <Grid.Column>
                  <strong>Digital Signature:</strong>
                  <div
                    style={{
                      marginTop: '5px',
                      fontFamily: 'monospace',
                      fontSize: '0.75em',
                      wordBreak: 'break-all'
                    }}
                  >
                    {event.metadata.signature.substring(0, 64)}...
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <strong>Signature Valid:</strong>
                  <div style={{ marginTop: '5px' }}>
                    {event.metadata.signatureValid === undefined && (
                      <Label color='grey'>Not Verified</Label>
                    )}
                    {event.metadata.signatureValid === true && (
                      <Label color='green'>
                        <Icon name='check circle' />
                        Valid
                      </Label>
                    )}
                    {event.metadata.signatureValid === false && (
                      <Label color='red'>
                        <Icon name='times circle' />
                        Invalid
                      </Label>
                    )}
                  </div>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* Tamper Detection Warning */}
        {event.metadata.signatureValid === false && (
          <Message negative>
            <Message.Header>
              <Icon name='exclamation triangle' />
              Tamper Detection Alert
            </Message.Header>
            <p style={{ marginTop: '10px' }}>
              This audit event failed signature verification, indicating possible tampering or
              data corruption. Treat this event with caution and investigate further.
            </p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button primary onClick={onClose}>
          <Icon name='close' />
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default AuditEventDetail;
